import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { usePatientCommunication } from '../hooks/usePatientCommunication'
import { ComingSoonPage } from '../pages/question/coming-soon/ComingSoon.page'

import type { ReactNode } from 'react'
import type { ESignDoctor } from '@/modules/appointment/types/e-sign.types'
import type { PatientCommunicationSpecialisation } from '../types/patient-communication.type'

export type StepData = Record<string, unknown>

type CareQuestionFlowConfig = {
  questionData: StepData
  setQuestionData: (update: StepData | ((previousData: StepData) => StepData)) => void
  specialisations?: PatientCommunicationSpecialisation[]
  doctors?: ESignDoctor[]
}

const initialData: StepData = {}
const initialSpecialisations: PatientCommunicationSpecialisation[] = []

export const CareQuestionFlowContext = createContext<CareQuestionFlowConfig>({
  questionData: initialData,
  setQuestionData: () => {},
  specialisations: initialSpecialisations,
})

type Props = {
  children: ReactNode
  requireSpecialisations?: boolean
}

export const CareQuestionFlowProvider = ({ children, requireSpecialisations = true }: Props) => {
  const [questionData, setQuestionData] = useState<StepData>(initialData)

  const {
    getCommunicationChannels: {
      value: patientCommunicationValue,
      execute: getCommunicationChannels,
      isLoading: patientCommunicationLoading,
    },
  } = usePatientCommunication()

  const { doctors, specialisations } = useMemo(() => {
    const questionChannel = patientCommunicationValue?.channels?.find((channel) => channel.type === 'question')

    return {
      doctors: questionChannel?.doctors || [],
      specialisations: questionChannel?.specialisations || [],
    }
  }, [patientCommunicationValue])

  // Load channels
  useEffect(() => {
    getCommunicationChannels()
  }, [])

  if (patientCommunicationLoading) {
    return <AnimatedLoader />
  }

  if (requireSpecialisations && doctors.length === 0) {
    return <ComingSoonPage />
  }

  return (
    <CareQuestionFlowContext.Provider
      value={{
        questionData,
        setQuestionData,
        specialisations,
        doctors,
      }}
    >
      {children}
    </CareQuestionFlowContext.Provider>
  )
}

export const useCareQuestionContext = () => useContext(CareQuestionFlowContext)
